import { KeyDocumentItem } from '@/utils/key-documents-utils';
import { RegisterItem } from '@/utils/register-items-utils';
import ComplianceProcessor from '@/js/external-js/variableModules/namespaces/AuditorExport/AuditorExport.class.js';
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PaywallDialog } from '../components/PaywallDialog';
// Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faHome,
  faSquareTerminal,
  faRobot,
  faBookOpen,
  faCogs,
  faLifeRing,
  faSend,
  faUser,
  faBuilding,
  faCalendar,
  faFileAlt,
  faClipboard,
  faNotebook,
  faBarsProgress,
  faCabinetFiling,
  faFileChartColumn,
  faShieldCheck,
  faLayerGroup,
  faObjectsColumn,
  faClipboardListCheck,
  faFolderMagnifyingGlass,
  faSolarSystem,
  faBooks,
  faTruckContainer,
  faDatabase,
  faUserShield,
  faGavel,
  faTriangleExclamation,
  faScrewdriverWrench,
  faFileCircleExclamation
} from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import { FeedbackDialog } from '../components/FeedbackDialog';
import { getAvailableFeatures } from '@/utils/featuresUtil';
import { routePathByName } from '@/app/routes';

/**
 * Simplified sidebar configuration for Starter Plan
 * Based on the design showing: Home, Compliance Calendar, Library (Policies, Processes, Controls),
 * Registers (Supplier, Data, Privacy), and Admin Tools (User Management, Company Settings)
 */
function getStarterPlanSidebarConfig(hasAuditCentre: boolean = false) {
  // DED-1173: SMB1001 Platinum and Diamond levels need Audit Centre (top two tiers only)
  const managementSystemItems: any[] = [
    {
      title: "Compliance Calendar",
      url: "/compliance-calendar",
      routeName: "compliance-calendar",
      icon: (props: any) => <FontAwesomeIcon icon={faCalendar} {...props} />,
    },
    {
      title: "Library",
      url: "#",
      icon: (props: any) => <FontAwesomeIcon icon={faBooks} {...props} />,
      isActive: true,
      items: [
        {
          title: "Policies",
          url: "/policies",
          routeName: "policies",
          icon: (props: any) => <FontAwesomeIcon icon={faClipboard} {...props} />,
        },
        {
          title: "Processes",
          url: "/processes",
          routeName: "processes",
          icon: (props: any) => <FontAwesomeIcon icon={faBarsProgress} {...props} />,
        },
        {
          title: "Controls",
          url: "/soa",
          routeName: "soa",
          icon: (props: any) => <FontAwesomeIcon icon={faShieldCheck} {...props} />,
        },
      ],
    },
    {
      title: "Registers",
      url: "#",
      icon: (props: any) => <FontAwesomeIcon icon={faCabinetFiling} {...props} />,
      isActive: true,
      items: [
        {
          title: "Supplier",
          url: "/supplier-register",
          routeName: "supplier-register",
          icon: (props: any) => <FontAwesomeIcon icon={faTruckContainer} {...props} />,
        },
        {
          title: "Data",
          url: "/dataRegister",
          routeName: "dataRegister",
          icon: (props: any) => <FontAwesomeIcon icon={faDatabase} {...props} />,
        },
        {
          title: "Privacy",
          url: "/privacy-request-register",
          routeName: "privacy-request-register",
          icon: (props: any) => <FontAwesomeIcon icon={faUserShield} {...props} />,
        },
      ],
    },
  ];

  // DED-1173: Add Audit Centre for SMB1001 Platinum and Diamond only (top two tiers)
  // DED-1141: Removed Incidents from Audit Centre - moved to Registers
  if (hasAuditCentre) {
    managementSystemItems.push({
      title: "Audit Centre",
      url: "/auditcentre/audits",
      routeName: "auditcentre.audits",
      icon: (props: any) => <FontAwesomeIcon icon={faFolderMagnifyingGlass} {...props} />,
      isActive: false,
      items: [
        {
          title: "Audits",
          url: "/auditcentre/audits",
          routeName: "auditcentre.audits",
          icon: (props: any) => <FontAwesomeIcon icon={faGavel} {...props} />,
          isActive: false,
        },
        {
          title: "Findings",
          url: "/auditcentre/findings",
          routeName: "auditcentre.findings",
          icon: (props: any) => <FontAwesomeIcon icon={faFileCircleExclamation} {...props} />,
          isActive: false,
        },
        {
          title: "Corrective Actions",
          url: "/auditcentre/corrective-actions",
          routeName: "auditcentre.corrective-actions",
          icon: (props: any) => <FontAwesomeIcon icon={faScrewdriverWrench} {...props} />,
          isActive: false,
        },
      ],
    });
  }

  return {
    user: {
      name: "shadcn",
      email: "m@example.com",
      avatar: "/avatars/shadcn.jpg",
    },
    home: [
      {
        title: "Home",
        url: "/dashboard",
        routeName: "dashboard",
        icon: (props: any) => <FontAwesomeIcon icon={faHome} {...props} />,
      },
    ],
    // Starter Plan has no Policy Management section
    policyManagement: [],
    managementSystem: managementSystemItems,
    // Starter Plan has no Reporting section
    reporting: [],
    adminTools: [
      {
        title: "Admin Tools",
        url: "#",
        icon: (props: any) => <FontAwesomeIcon icon={faCogs} {...props} />,
        isActive: true,
        items: [
          {
            title: "User Management",
            url: "/usermanagement",
            routeName: "user-management",
            icon: (props: any) => <FontAwesomeIcon icon={faUser} {...props} />,
          },
          {
            title: "Company Settings",
            url: "/settings/general",
            routeName: "settings.general",
            icon: (props: any) => <FontAwesomeIcon icon={faBuilding} {...props} />,
          },
        ],
      },
    ],
    navSecondary: [
      {
        title: "Support",
        url: "https://help.deiterate.com",
        icon: (props: any) => <FontAwesomeIcon icon={faLifeRing} {...props} />,
      },
      {
        title: "Feedback",
        url: "#",
        icon: (props: any) => <FontAwesomeIcon icon={faSend} {...props} />,
        component: true,
        renderComponent: (item: any) => {
          return (
            <FeedbackDialog>
              <button className="flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors">
                <FontAwesomeIcon icon={faSend} className="h-4 w-4 shrink-0" />
                <span className="truncate">Feedback</span>
              </button>
            </FeedbackDialog>
          );
        },
      },
    ],
  };
}

export function getSidebarConfig(customerPlan: string = 'default', keyDocumentItems?: KeyDocumentItem[], registerItems?: RegisterItem[], openAuditorExport?: () => void, currentRoute?: string, smb1001Level?: string) {
  // Check if we're on the Essential Eight or Privacy Act route
  const isEssentialEightRoute = window.location.pathname === '/essential-eight';
  const isPrivacyActRoute = window.location.pathname === '/privacy-act';
  
  // Check if this is a Starter Plan
  const isStarterPlan = customerPlan === 'starter' || customerPlan === 'Starter' || customerPlan === 'Starter Plan';
  console.log('[Sidebar] Customer plan:', customerPlan);
  console.log('[Sidebar] Is Starter Plan:', isStarterPlan);
  
  // Return simplified sidebar for Starter Plan
  if (isStarterPlan) {
    // DED-1173: Audit Centre only available for Platinum and Diamond tiers (top two only)
    const auditCentreLevels = ['Platinum', 'Diamond'];
    const hasAuditCentre = !!smb1001Level && auditCentreLevels.includes(smb1001Level);
    console.log('[Sidebar] Starter Plan SMB1001 Level:', smb1001Level, 'hasAuditCentre:', hasAuditCentre);
    return getStarterPlanSidebarConfig(hasAuditCentre);
  }

  // Function to wrap navigation items with paywall dialog
  const wrapWithPaywall = (item: any) => {
    // Special case: always allow dashboard to work normally
    if (item.url === '/dashboard') {
      return item;
    }

    // Special case: parent menu items with '#' should still toggle
    if (item.url === '#') {
      return item;
    }

    // Create a copy of the item
    const processedItem = { ...item };

    console.log('Wrapping item:', item.title || item.url);

    // For items with a component already (like Feedback)
    if (item.component && item.renderComponent) {
      const originalRender = item.renderComponent;
      processedItem.renderComponent = (itemProps: any) => {
        // Use a wrapper component to manage state
        function WrappedComponent() {
          const [dialogOpen, setDialogOpen] = useState(false);

          return (
            <>
              <div onClick={(e) => {
                e.preventDefault();
                e.stopPropagation(); // Prevent event bubbling
                setDialogOpen(true);
              }}>
                {originalRender(itemProps)}
              </div>
              <PaywallDialog open={dialogOpen} onOpenChange={setDialogOpen} />
            </>
          );
        }

        return <WrappedComponent />;
      };
      return processedItem;
    }

    // For regular links, we need to maintain the URL but override the click behavior
    processedItem.component = true;
    processedItem.originalUrl = item.url;

    // We'll store the URL but override how clicks are handled
    // Don't delete the URL as it's needed for isActive styling

    // Add render component that shows dialog instead of navigating
    processedItem.renderComponent = () => {
      // Custom component that intercepts clicks
      const PaywallLink = () => {
        const [dialogOpen, setDialogOpen] = useState(false);
        const navigate = useNavigate();

        return (
          <>
            <a
              href={processedItem.originalUrl}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation(); // Prevent event bubbling
                setDialogOpen(true);
                return false; // Prevent default and stop propagation
              }}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              {processedItem.icon && <processedItem.icon />}
              <span>{processedItem.title}</span>
            </a>
            <PaywallDialog open={dialogOpen} onOpenChange={setDialogOpen} />
          </>
        );
      };

      return <PaywallLink />;
    };

    return processedItem;
  };

  // Process all sidebar items with paywall wrapper if on Essential Eight or Privacy Act route
  const processWithPaywall = (items: any[]) => {
    if (!isEssentialEightRoute && !isPrivacyActRoute) return items;
    return items.map(item => {
      // Always allow dashboard to work normally
      if (item.url === '/dashboard') return item;
      return wrapWithPaywall(item);
    });
  };

  // Process nested items
  const processNestedItems = (items: any[]) => {
    if (!isEssentialEightRoute && !isPrivacyActRoute) return items;

    return items.map(item => {
      const processedItem = { ...item };

      // For parent menu items with '#', just process the children
      if (item.url === '#') {
        // Process nested items if they exist
        if (processedItem.items && Array.isArray(processedItem.items)) {
          processedItem.items = processedItem.items.map(nestedItem => {
            // Always allow dashboard to work normally
            if (nestedItem.url === '/dashboard') return nestedItem;
            return wrapWithPaywall(nestedItem);
          });
        }
        return processedItem;
      }

      // For other items, process both the parent and children
      if (processedItem.items && Array.isArray(processedItem.items)) {
        processedItem.items = processedItem.items.map(nestedItem => {
          // Always allow dashboard to work normally
          if (nestedItem.url === '/dashboard') return nestedItem;
          return wrapWithPaywall(nestedItem);
        });
      }

      // Also process the parent item itself if it's not a dashboard link
      return processedItem.url === '/dashboard' ? processedItem : wrapWithPaywall(processedItem);
    });
  };

  // Base configuration
  const config = {
    user: {
      name: "shadcn",
      email: "m@example.com",
      avatar: "/avatars/shadcn.jpg",
    },
    home: [
      {
        title: "Home",
        url: "/dashboard",
        routeName: "dashboard",
        icon: (props) => <FontAwesomeIcon icon={faHome} {...props} />,
      },
    ],
    policyManagement: [
      {
        title: "Policy Management",
        url: "#",
        icon: (props) => <FontAwesomeIcon icon={faSquareTerminal} {...props} />,
        isActive: true,
        items: [
          {
            title: "Policy Admin",
            url: "/policyadmin",
            routeName: "policy-admin",
            icon: (props) => <FontAwesomeIcon icon={faClipboard} {...props} />,
          },
          ...(customerPlan !== 'enterprise' && customerPlan !== 'Enterprise Plan' && customerPlan !== 'starter' && customerPlan !== 'Starter' && customerPlan !== 'Starter Plan' ? [{
            title: "Policy Reader",
            url: !!getAvailableFeatures().find(f => f.name === 'Policy Reader') ? '/policy-reader' : `https://app.${window.location.hostname.split('.').slice(-2).join('.')}`,
            ...(!!getAvailableFeatures().find(f => f.name === 'Policy Reader') ? { routeName: "policy-reader-index" } : {}),
            icon: (props) => <FontAwesomeIcon icon={faNotebook} {...props} />,
          }] : []),

        ],
      },
    ],
    managementSystem: (() => {
      const isEnterprise = customerPlan === 'enterprise' || customerPlan === 'Enterprise Plan';
      const availableFeatures = getAvailableFeatures();
      const hasAssuranceProgram = !!availableFeatures.find(f => f.name === 'Assurance Program');
      const hasAuditCentre = !!availableFeatures.find(f => f.name === 'Audit Centre');
      console.debug('Available features:', availableFeatures);

      const assuranceProgram = {
        title: "Assurance Program",
        url: "/assurancelibrary",
        routeName: "assurance-library",
        icon: (props) => <FontAwesomeIcon icon={faObjectsColumn} {...props} />,
        isActive: false,
        items: [
          {
            title: "Tasks",
            url: "/assurancelibrary",
            routeName: "assurance-library",
            icon: (props) => <FontAwesomeIcon icon={faBarsProgress} {...props} />,
            isActive: false,
          },
          {
            title: "Checklists",
            url: "/checklists",
            routeName: "checklists",
            icon: (props) => <FontAwesomeIcon icon={faClipboardListCheck} {...props} />,
            isActive: false,
          },
        ],
      };

      const assuranceTasks = {
        title: "Assurance Tasks",
        url: "/assurancelibrary",
        routeName: "assurance-library",
        icon: (props) => <FontAwesomeIcon icon={faBarsProgress} {...props} />,
        isActive: false,
      };

      // DED-1141: Removed Incidents from Audit Centre - moved to Registers
      const auditCentre = {
        title: "Audit Centre",
        url: "/auditcentre/audits",
        routeName: "auditcentre.audits",
        icon: (props) => <FontAwesomeIcon icon={faFolderMagnifyingGlass} {...props} />,
        isActive: false,
        items: [
          {
            title: "Audits",
            url: "/auditcentre/audits",
            routeName: "auditcentre.audits",
            icon: (props) => <FontAwesomeIcon icon={faGavel} {...props} />,
            isActive: false,
          },
          {
            title: "Findings",
            url: "/auditcentre/findings",
            routeName: "auditcentre.findings",
            icon: (props) => <FontAwesomeIcon icon={faFileCircleExclamation} {...props} />,
            isActive: false,
          },
          {
            title: "Corrective Actions",
            url: "/auditcentre/corrective-actions",
            routeName: "auditcentre.corrective-actions",
            icon: (props) => <FontAwesomeIcon icon={faScrewdriverWrench} {...props} />,
            isActive: false,
          },
        ],
      };
      // Base management system items (excluding Registers for enterprise)
      const baseItems = [
        {
          title: "Compliance Calendar",
          url: "/compliance-calendar",
          routeName: "compliance-calendar",
          icon: (props) => <FontAwesomeIcon icon={faCalendar} {...props} />,
        },
        ...([
          hasAssuranceProgram ? assuranceProgram : null,
          hasAuditCentre ? auditCentre : null
        ].filter(Boolean).length > 0 ? [
          ...(hasAssuranceProgram ? [assuranceProgram] : []),
          ...(hasAuditCentre ? [auditCentre] : [])
        ] : [assuranceTasks]),
        // Registers will be added after key documents for enterprise
        ...(customerPlan !== 'enterprise' && customerPlan !== 'Enterprise Plan' ? [{
          title: "Registers",
          url: "/registers",
          routeName: "registers",
          icon: (props) => <FontAwesomeIcon icon={faCabinetFiling} {...props} />,
          isActive: false,
          items: registerItems || [],
        }] : []),
      ];

      // Registers item to be added after key documents for enterprise
      const registersItem = {
        title: "Registers",
        url: "/registers",
        routeName: "registers",
        icon: (props: any) => <FontAwesomeIcon icon={faCabinetFiling} {...props} />,
        isActive: false,
        items: registerItems || [],
      };

      // For enterprise, add key documents directly to the sidebar
      if (isEnterprise && keyDocumentItems?.length) {
        // Create a new array with properly formatted key document items
        const formattedKeyDocs = keyDocumentItems.map(doc => {
          // If icon is a React component, use it directly
          if (typeof doc.icon === 'function') {
            return {
              ...doc,
              icon: doc.icon
            };
          }
          // If icon is a React element, create a function that returns it
          if (doc.icon && typeof doc.icon === 'object' && '$$typeof' in doc.icon) {
            return {
              ...doc,
              // icon: () => doc.icon as React.ReactNode
            };
          }
          // Default to file icon if no valid icon is provided
          return {
            ...doc,
            // icon: (props: any) => <FontAwesomeIcon icon={faFileAlt} {...props} />
          };
        });

        return [
          // Add the management system section
          {
            title: "Management System",
            url: "#",
            icon: (props: any) => <FontAwesomeIcon icon={faRobot} {...props} />,
            isActive: true,
            items: [
              ...baseItems,
              ...formattedKeyDocs,
              ...(customerPlan === 'enterprise' || customerPlan === 'Enterprise Plan' ? [registersItem] : [])]
          },
        ];
      }

      // For non-enterprise or when no key documents, use the standard layout
      return [
        {
          title: "Management System",
          url: "#",
          icon: (props) => <FontAwesomeIcon icon={faRobot} {...props} />,
          isActive: true,
          items: [
            ...baseItems,
            // Add key documents as a dropdown
            {
              title: "Key Documents",
              url: "/key-documents",
              routeName: "key-documents",
              icon: (props) => <FontAwesomeIcon icon={faFileAlt} {...props} />,
              isActive: false,
              items: keyDocumentItems || [],
            }
          ],
        },
      ];
    })(),
    reporting: (() => {
      const isEnterprise = customerPlan === 'enterprise' || customerPlan === 'Enterprise Plan';

      const base = [
        {
          title: "Reporting",
          url: "#",
          icon: (props: any) => <FontAwesomeIcon icon={faBookOpen} {...props} />,
          isActive: true,
          items: [
          ],
        },
      ];

      if (!isEnterprise) {
        base[0].items.push({
          title: "Report Library",
          url: "/report-library",
          routeName: "report-library",
          icon: (props: any) => <FontAwesomeIcon icon={faFileChartColumn} {...props} />,
        });
      } else {
        base[0].items.push({
          title: "Auditor Pack",
          url: '',
          onClick: (e) => {
            e.preventDefault();
            // Open the modern auditor export dialog
            if (openAuditorExport) {
              openAuditorExport();
            }
          },
          icon: (props: any) => <FontAwesomeIcon icon={faFileChartColumn} {...props} />,
        });
      }

      return base;
    })(),
    adminTools: [
      {
        title: "Admin Tools",
        url: "#",
        icon: (props: any) => <FontAwesomeIcon icon={faCogs} {...props} />,
        isActive: true,
        items: [
          {
            title: "User Management",
            url: "/usermanagement",
            routeName: "user-management",
            icon: (props) => <FontAwesomeIcon icon={faUser} {...props} />,
          },
          {
            title: "Company Settings",
            url: "/settings/general",
            routeName: "settings.general",
            icon: (props) => <FontAwesomeIcon icon={faBuilding} {...props} />,
          },
         ...(getAvailableFeatures().find((feature) => feature.name === "System Updates") ? [
          {
            title: "System Updates",
            url: "/system-update",
            routeName: "system-update",
            icon: (props) => <FontAwesomeIcon icon={faSolarSystem} {...props} />,
          }
         ] : [])
        ],
      },
    ],
    navSecondary: [
      {
        title: "Support",
        url: "https://help.deiterate.com",
        icon: (props) => <FontAwesomeIcon icon={faLifeRing} {...props} />,
      },
      {
        title: "Feedback",
        url: "#",
        icon: (props) => <FontAwesomeIcon icon={faSend} {...props} />,
        component: true, // This indicates we're using a custom component
        renderComponent: (item) => {
          return (
            <FeedbackDialog>
              <button className="flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors">
                <FontAwesomeIcon icon={faSend} className="h-4 w-4 shrink-0" />
                <span className="truncate">Feedback</span>
              </button>
            </FeedbackDialog>
          );
        },
      },
    ]
  };

  // If on Essential Eight route, process all sections to add paywall dialogs
  if (isEssentialEightRoute) {
    // For top-level arrays without nesting
    config.home = processWithPaywall(config.home);
    config.navSecondary = processWithPaywall(config.navSecondary);

    // For nested items (parent items with children)
    config.policyManagement = processNestedItems(config.policyManagement);
    config.managementSystem = processNestedItems(config.managementSystem);
    config.reporting = processNestedItems(config.reporting);
    config.adminTools = processNestedItems(config.adminTools);
  }

  return config;
}
